import { BOOKING_DRAFT_STORAGE_KEY } from "./bookingStorageKeys";

/** Non-empty string / non-empty array / defined number / object with keys */
function isMeaningfulOverlayValue(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** Merge first ride leg: draft only overwrites quote when overlay has real values (not empty strings). */
function mergeFirstRideLeg(quoteLeg, draftLeg) {
  const q = quoteLeg && typeof quoteLeg === "object" ? quoteLeg : {};
  const d = draftLeg && typeof draftLeg === "object" ? draftLeg : {};
  const merged = { ...q };
  for (const [k, val] of Object.entries(d)) {
    if (isMeaningfulOverlayValue(val)) {
      merged[k] = val;
    }
  }
  return merged;
}

export function pickNonEmptyDraftOrQuote(draftVal, quoteVal) {
  return isMeaningfulOverlayValue(draftVal) ? draftVal : quoteVal;
}

/**
 * Return schedule on root or return_date/time on first ride (Check Fare round trip shape).
 */
export function hasRoundTripLegData(booking) {
  if (!booking || typeof booking !== "object") return false;
  const r0 = booking.rides?.[0] || booking.trips?.[0] || {};
  return (
    (booking.return_schedule != null &&
      String(booking.return_schedule).trim() !== "") ||
    (Boolean(r0.return_date) &&
      Boolean(r0.return_time) &&
      String(r0.return_date).trim() !== "" &&
      String(r0.return_time).trim() !== "")
  );
}

/**
 * Merge last Check Fare quote with form-written draft (same ride_type).
 * Quote wins for fare_details when present.
 * Draft does not wipe quote fields with "" or partial rides[] (fixes checkout showing defaults).
 */
export function mergeBookingDataWithQuote(bookingData, draftData) {
  if (!draftData || typeof draftData !== "object") return bookingData;
  if (!bookingData?.ride_type) return draftData;
  if (Number(draftData.ride_type) !== Number(bookingData.ride_type)) {
    return bookingData;
  }

  const qRides = Array.isArray(bookingData.rides) ? bookingData.rides : [];
  const dRides = Array.isArray(draftData.rides) ? draftData.rides : [];
  const mergedFirst = mergeFirstRideLeg(qRides[0], dRides[0]);
  const rides =
    qRides.length > 0 || dRides.length > 0
      ? [mergedFirst, ...qRides.slice(1)]
      : draftData.rides ?? bookingData.rides;

  return {
    ...bookingData,
    ...draftData,
    rides,
    pickup: pickNonEmptyDraftOrQuote(draftData.pickup, bookingData.pickup),
    dropoff: pickNonEmptyDraftOrQuote(draftData.dropoff, bookingData.dropoff),
    schedule: pickNonEmptyDraftOrQuote(draftData.schedule, bookingData.schedule),
    return_schedule: pickNonEmptyDraftOrQuote(
      draftData.return_schedule,
      bookingData.return_schedule,
    ),
    return_pickup: pickNonEmptyDraftOrQuote(
      draftData.return_pickup,
      bookingData.return_pickup,
    ),
    return_dropoff: pickNonEmptyDraftOrQuote(
      draftData.return_dropoff,
      bookingData.return_dropoff,
    ),
    pickup_lat: draftData.pickup_lat ?? bookingData.pickup_lat,
    pickup_lng: draftData.pickup_lng ?? bookingData.pickup_lng,
    dropoff_lat: draftData.dropoff_lat ?? bookingData.dropoff_lat,
    dropoff_lng: draftData.dropoff_lng ?? bookingData.dropoff_lng,
    return_pickup_lat:
      draftData.return_pickup_lat ?? bookingData.return_pickup_lat,
    return_pickup_lng:
      draftData.return_pickup_lng ?? bookingData.return_pickup_lng,
    return_dropoff_lat:
      draftData.return_dropoff_lat ?? bookingData.return_dropoff_lat,
    return_dropoff_lng:
      draftData.return_dropoff_lng ?? bookingData.return_dropoff_lng,
    stops:
      Array.isArray(draftData.stops) && draftData.stops.length > 0
        ? draftData.stops
        : bookingData.stops,
    return_stops:
      Array.isArray(draftData.return_stops) && draftData.return_stops.length > 0
        ? draftData.return_stops
        : bookingData.return_stops,
    no_of_passengers:
      draftData.no_of_passengers != null
        ? draftData.no_of_passengers
        : bookingData.no_of_passengers,
    suitcase:
      draftData.suitcase != null ? draftData.suitcase : bookingData.suitcase,
    fare_details: bookingData.fare_details ?? draftData.fare_details,
    booking_type: (() => {
      const dq = draftData.booking_type;
      const bq = bookingData.booking_type;
      if (dq == null) return bq;
      // Stale draft can stay booking_type 1 after user ran Check Fare on round trip — keep quote 2.
      if (
        Number(bq) === 2 &&
        hasRoundTripLegData(bookingData) &&
        Number(dq) === 1
      ) {
        return bq;
      }
      return dq;
    })(),
  };
}

/**
 * Strip round-trip fields when booking_type is one-way (stale merge / session).
 */
export function sanitizeOneWayBookingForCheckout(data) {
  if (!data || typeof data !== "object") return data;
  if (Number(data.booking_type) !== 1) return data;

  const rides = Array.isArray(data.rides)
    ? data.rides.map((r, idx) => {
        if (idx !== 0 || !r || typeof r !== "object") return r;
        const {
          return_date,
          return_time,
          return_pickup_lat,
          return_pickup_lng,
          return_drop_lat,
          return_drop_lng,
          return_stops,
          ...rest
        } = r;
        return rest;
      })
    : data.rides;

  return {
    ...data,
    rides,
    return_schedule: null,
    return_pickup: null,
    return_pickup_lat: null,
    return_pickup_lng: null,
    return_dropoff: null,
    return_dropoff_lat: null,
    return_dropoff_lng: null,
    return_stops: undefined,
  };
}

/**
 * One-way cleanup + downgrade inconsistent round-trip (booking_type 2 but no return leg in payload).
 * Fixes stale session where booking_type stayed 2 after switching to one-way.
 */
export function sanitizeCheckoutBookingPayload(data) {
  if (!data || typeof data !== "object") return data;
  let d = sanitizeOneWayBookingForCheckout(data);
  if (Number(d.booking_type) !== 2) return d;
  if (!hasRoundTripLegData(d)) {
    return sanitizeOneWayBookingForCheckout({ ...d, booking_type: 1 });
  }
  return d;
}

/**
 * @param {Storage | null | undefined} storage — inject mock in tests
 */
export function mergeBookingDataWithSessionDraft(bookingData, storage) {
  const s =
    storage ??
    (typeof sessionStorage !== "undefined" ? sessionStorage : null);
  if (!s) return bookingData;
  try {
    const raw = s.getItem(BOOKING_DRAFT_STORAGE_KEY);
    if (!raw) return bookingData;
    const parsed = JSON.parse(raw);
    return mergeBookingDataWithQuote(bookingData, parsed?.data);
  } catch {
    return bookingData;
  }
}

/**
 * Which payload checkout should show (mirrors Checkout useMemo).
 * @param {object} params
 * @param {number} params.navRev — location.state._checkoutRevision or 0
 * @param {boolean} params.hasBookingPayloadInState
 * @param {*} params.stateBookingData — location.state.bookingData
 * @param {{ ts: number, data: object } | null} params.draft — parsed BOOKING_DRAFT or null
 * @param {string | null} params.checkoutBookingRaw — raw CHECKOUT_BOOKING storage or null
 */
export function resolveCheckoutBookingData({
  navRev,
  hasBookingPayloadInState,
  stateBookingData,
  draft,
  checkoutBookingRaw,
}) {
  // Navigation payload (Policy Proceed / router state) wins over session draft + storage.
  if (hasBookingPayloadInState && stateBookingData != null) {
    return {
      data: sanitizeCheckoutBookingPayload(stateBookingData),
      source: "state",
    };
  }
  if (
    draft?.data &&
    typeof draft.ts === "number" &&
    navRev > 0 &&
    draft.ts > navRev
  ) {
    return {
      data: sanitizeCheckoutBookingPayload(draft.data),
      source: "draft",
    };
  }
  try {
    const resolved = checkoutBookingRaw ? JSON.parse(checkoutBookingRaw) : null;
    return {
      data: sanitizeCheckoutBookingPayload(resolved),
      source: "storage",
    };
  } catch {
    return { data: null, source: "storage_error" };
  }
}

